import { Box, useTheme } from '@mui/material'
import { useTranslations } from 'next-intl'

import { useSettings } from '@core/context/settingsContext'

import Link from '~/components/Link'
import { useCurrentOrg } from '~/data/api/hooks/organizations'
import routeDirectory from '~/navigation/routes'

const defaultLogoUrl = '/images/logos/synop-logo-600-alpha.png'
const defaultMiniLogoUrl = '/images/logos/synop-logo-mini.png'

/**
 * Renders the same DOM in both expanded and collapsed sidebar states.
 * The wordmark fades via opacity, and the bolt transitions its size/position
 * between sitting over the "y" in "Synop" (expanded) and being centered on
 * the 68px rail (collapsed). Keeping a single stable tree is what lets the
 * bolt animate smoothly instead of snapping when the sidebar toggles.
 */
export default function HeaderBranding() {
  const { navCollapsed: boltOnly } = useSettings().settings
  const { org } = useCurrentOrg()
  const theme = useTheme()

  // This component renders inside NavThemeProvider, so palette.mode reflects the org's real
  // DAY/NIGHT theme. In dark mode prefer the dark asset and fall back to the light one.
  const isDarkMode = theme.palette.mode === 'dark'
  const orgLogoUrl = (isDarkMode ? org?.logoUrlDark : undefined) ?? org?.logoUrlLight ?? org?.logoUrl
  const orgIconUrl = (isDarkMode ? org?.iconUrlDark : undefined) ?? org?.iconUrlLight ?? org?.iconUrl
  const hasOrgLogo = Boolean(orgLogoUrl)
  const logoUrl = orgLogoUrl || defaultLogoUrl
  const t = useTranslations('layouts.navDrawer')

  return (
    <Link
      href={routeDirectory.index}
      aria-label={t('synopHome')}
      style={{ marginRight: boltOnly ? 0 : 10, display: 'inline-flex' }}
    >
      <Box
        sx={{
          position: 'relative',
          height: 40,
          width: boltOnly ? boltHeightCollapsed : 100,
          transition: theme.transitions.create(['width'])
        }}
      >
        <Box
          alt="Synop Logo"
          component="img"
          src={logoUrl}
          sx={{
            objectFit: 'contain',
            height: '100%',
            width: '100%',
            // The default wordmark is dark ink; render it white in dark mode.
            // Org-supplied logos are brand assets and are left untouched.
            ...(theme.palette.mode === 'dark' && !hasOrgLogo && { filter: 'brightness(0) invert(1)' }),
            // Hide the wordmark in the collapsed rail; the bolt takes over.
            opacity: boltOnly ? 0 : 1,
            transition: theme.transitions.create(['opacity'])
          }}
        />

        <Box
          alt="Synop Logo"
          component="img"
          src={orgIconUrl ?? defaultMiniLogoUrl}
          sx={{
            // Expanded: bolt nestles over the "y" in "Synop".
            // Collapsed: bolt grows + slides to fill the rail, centered at width=boltHeightLarge.
            position: 'absolute',
            top: boltOnly ? 2 : 5.5,
            left: boltOnly ? 0 : 17.5,
            // When the drawer is open with a custom org logo, hide the bolt (the org logo owns the space).
            opacity: hasOrgLogo && !boltOnly ? 0 : 1,
            height: boltOnly ? boltHeightCollapsed : boltHeightSmall,
            width: boltOnly ? boltHeightCollapsed : boltHeightSmall,
            transition: theme.transitions.create(['height', 'width', 'top', 'left', 'opacity'])
          }}
        />
      </Box>
    </Link>
  )
}

const boltHeightSmall = 36
// Sized to fit on the 68px collapsed rail alongside the expand-toggle button.
const boltHeightCollapsed = 36
